import { FC, useMemo } from "react";
import { AxisLabelsFormatterContextObject } from "highcharts";
import { Box } from "@mui/material";
import { MultiTypeHighcharts } from "@/storybook/c8r-highcharts/MultiTypeHighcharts";
import { useAppSelector } from "@/store/hooks";
import { moneyFormatter } from "@/utils/numeral/moneyFormatter";
import { HighchartsProvider } from "@/storybook/c8r-highcharts/common/components/HighchartsProvider";
import { resourceDetailsResourceBillingDataLoadingSelector } from "@/store/resource-details/selectors/billing-data/loadings/resourceDetailsResourceBillingDataLoadingSelector";
import { HighchartsChartType } from "@/storybook/c8r-highcharts/common/components/HighchartsChartTypeSelect";
import {
  HighchartsChartDataType,
  TooltipMarkupGenerator,
} from "@/storybook/c8r-highcharts/common/utils/types/common";
import { stackedChartHeadingLabelFormatter } from "@/storybook/c8r-highcharts/common/utils/helpers/stackedChartHeadingLabelFormatter";
import { Dates } from "@/services/cloudchipr.api";
import { organisationQuarterStartMonthSelector } from "@/store/common/selectors/org-current-settings/organisationQuarterStartMonthSelector";
import { getPieChartTooltipValueFormatter } from "@/storybook/c8r-highcharts/common/utils/helpers/getPieChartTooltipValueFormatter";
import { getResourceDetailsCostsChartTooltipValueFormatter } from "@/components/pages/common/resource-details/utils/helpers/getResourceDetailsCostsChartTooltipValueFormatter";

interface ResourceDetailsCostsChartProps {
  dates: Dates;
  chartType: HighchartsChartType;
  chartData?: HighchartsChartDataType | null;
}

export const ResourceDetailsCostsChart: FC<ResourceDetailsCostsChartProps> = ({
  chartType,
  chartData,
  dates,
}) => {
  const loading = useAppSelector(
    resourceDetailsResourceBillingDataLoadingSelector,
  );
  const quarterStartMonth = useAppSelector(
    organisationQuarterStartMonthSelector,
  );

  const totals = useMemo(() => {
    return calculateResourceDetailsChartTotals(chartData) ?? 0;
  }, [chartData]);

  const tooltipValueFormatter: TooltipMarkupGenerator = useMemo(
    () => getResourceDetailsCostsChartTooltipValueFormatter(totals, chartData),
    [chartData, totals],
  );

  const pieChartTooltipValueFormatter: TooltipMarkupGenerator = useMemo(
    () => getPieChartTooltipValueFormatter(dates, quarterStartMonth),
    [dates, quarterStartMonth],
  );

  return (
    <Box height={260}>
      <HighchartsProvider>
        <MultiTypeHighcharts
          showLegends
          maxHeight={260}
          chartData={chartData}
          chartType={chartType}
          loading={loading ? loadingProps : false}
          yAxisLabelsFormatter={yAxisLabelsFormatter}
          tooltipMarkupGenerator={
            chartType === "pie"
              ? pieChartTooltipValueFormatter
              : tooltipValueFormatter
          }
          noDataMessage="No data by your selected date range and filters."
          stackedChartHeadingLabelFormatter={stackedChartHeadingLabelFormatter}
        />
      </HighchartsProvider>
    </Box>
  );
};

const yAxisLabelsFormatter = (params: AxisLabelsFormatterContextObject) =>
  `${moneyFormatter(+params.value)}`;

const loadingProps = { showLoader: true };

const calculateResourceDetailsChartTotals = (
  chartData?: null | HighchartsChartDataType,
): Record<number, number> => {
  const totals: Record<number, number> = {};
  const numPoints = chartData?.categories?.length ?? 0;

  for (let i = 0; i < numPoints; i++) {
    let total = 0;
    chartData?.series?.forEach((series) => {
      const value = series.data[i]?.y ?? 0;

      total += value;
    });
    totals[i] = total;
  }

  return totals;
};
